import { Button } from '@/components'
import React, { useState } from 'react'
import styled from 'styled-components'

interface ConfirmProps {
  title?: string
  message: string | React.ReactNode
  confirmBtnName: string
  cancelBtnName: string
  onConfirm: (inputValue?: string) => void
  onCancel: () => void
  inputPlaceholder?: string
  inputRequired?: boolean
  inputValidator?: (value: string) => true | string
}

const DimBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  pointer-events: none; /* 배경이 클릭을 방해하지 않도록 */
`

const ConfirmWrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  width: 450px;
  min-height: 200px;
  max-height: 500px;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  pointer-events: auto; /* 다이얼로그는 클릭 가능하도록 */
`

const Title = styled.h3`
  font-size: 25px;
  font-weight: bold;
  margin: 10px 0 10px;
  color: #333;
`

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #ccc;
  margin: 10px 0;
`

const MessageWrapper = styled.div`
  flex-grow: 1;
  overflow: auto;
  max-height: 300px;
  padding-bottom: 10px;
`

const Message = styled.div`
  font-size: 14px;
  color: #666;
  margin: 3px 0 20px;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  flex-grow: 1;
`

const Input = styled.input`
  width: 100%;
  padding: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  border: 1px solid #ff0000; /* 임시로 빨간 테두리 */
  border-radius: 4px;
  height: 40px; /* 명시적 높이 */
`

const ErrorText = styled.div`
  color: red;
  font-size: 12px;
  margin-bottom: 10px;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  justify-content: space-between;
  padding: 10px;
  background-color: #fff;
  position: sticky;
  bottom: 0;
  left: 0;
  width: 100%;
`

const Confirm: React.FC<ConfirmProps> = ({
  title,
  message,
  confirmBtnName,
  cancelBtnName,
  onConfirm,
  onCancel,
  inputPlaceholder,
  inputRequired = false,
  inputValidator,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState('')

  const handleConfirm = () => {
    // inputPlaceholder가 있거나 inputRequired가 true면 입력 검증
    if (inputPlaceholder || inputRequired) {
      if (inputValidator) {
        const result = inputValidator(inputValue)
        if (result !== true) {
          setError(result as string)
          return
        }
      }
      onConfirm(inputValue)
    } else {
      onConfirm()
    }
  }

  return (
    <>
      <DimBackground />
      <ConfirmWrapper>
        <Title>{title}</Title>
        {title && message && <Separator />}
        <MessageWrapper>
          <Message>{message}</Message>
          {/* inputPlaceholder가 있거나 inputRequired가 true면 입력 필드 표시 */}
          {(inputPlaceholder || inputRequired) && (
            <>
              <Input
                type="text"
                placeholder={inputPlaceholder || '입력하세요'}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value)
                  setError('')
                }}
                onKeyDown={(e) => {
                  e.stopPropagation()
                }}
              />
              <div style={{ color: 'red', fontSize: '12px' }}>
                현재 입력값: {inputValue}
              </div>
              {error && <ErrorText>{error}</ErrorText>}
            </>
          )}
        </MessageWrapper>
        <ButtonGroup>
          <Button onClick={handleConfirm} color="neverbe" width="100%">
            {confirmBtnName}
          </Button>
          <Button
            onClick={onCancel}
            color="neverbe"
            variant="outlined"
            width="100%"
          >
            {cancelBtnName}
          </Button>
        </ButtonGroup>
      </ConfirmWrapper>
    </>
  )
}

export default Confirm
